import type { PlantaUGAtMinutes } from "../components/SunBurstChart";
import type {
  AggregatedInversor,
  BaseUsinaValue,
} from "../models/dados-indicadores.model";
import { getNumberOfUgName } from "../../usinas/ugsMapper";
import type { UG } from "../../usinas/types";
import { mergePlantaUGsAtMinutesWithUsinaConfig } from "./mergePlantaUGsWithUsinaConfig";

type InverterIdentity = {
  ugNumber?: number;
  inverterNumber?: number;
};

function getInverterNumber(name?: string): number | undefined {
  const match = name?.match(/INV\s*(\d+)/i);
  return match ? Number(match[1]) : undefined;
}

function normalizeName(name?: string): string {
  return (name ?? "").trim().toLowerCase();
}

function normalizeEquipmentName(name?: string): string {
  return normalizeName(name).replace(/\s+/g, "");
}

function getInverterKey(identity: InverterIdentity, name?: string): string {
  if (identity.ugNumber !== undefined && identity.inverterNumber !== undefined) {
    return `${identity.ugNumber}:${identity.inverterNumber}`;
  }
  return `name:${normalizeName(name)}`;
}

function getUgDisplayName(name?: string, fallbackNumber?: number): string {
  const ugNumber = getNumberOfUgName(name) ?? fallbackNumber;
  return ugNumber !== undefined ? `UG ${ugNumber}` : name ?? "UG";
}

function sortConfiguredUGs(configuredUGs: UG[]): UG[] {
  return [...configuredUGs].sort((a, b) => {
    const aNum = getNumberOfUgName(a.name) ?? Number.MAX_SAFE_INTEGER;
    const bNum = getNumberOfUgName(b.name) ?? Number.MAX_SAFE_INTEGER;
    return aNum - bNum;
  });
}

function hasMinuteSeries(minutes?: BaseUsinaValue[]): boolean {
  return (minutes?.length ?? 0) > 0;
}

function shouldUseUgPrefixInNames(
  configuredUGs: UG[] | undefined,
  sampleNames: string[],
): boolean {
  if ((configuredUGs?.length ?? 0) > 1) {
    if (sampleNames.some((name) => /UG/i.test(name ?? ""))) {
      return true;
    }

    const inverterNumbers = sampleNames
      .map((name) => getInverterNumber(name))
      .filter((value): value is number => value !== undefined);

    if (inverterNumbers.length > new Set(inverterNumbers).size) {
      return true;
    }

    return true;
  }

  return sampleNames.some((name) => /UG/i.test(name ?? ""));
}

function formatInverterName(
  inverterNumber: number,
  ugLabel: string,
  useUgPrefix: boolean,
  existingName?: string,
): string {
  const invNumber = getInverterNumber(existingName) ?? inverterNumber;
  const baseName = `INV ${invNumber}`;
  return useUgPrefix ? `${ugLabel} - ${baseName}` : baseName;
}

export function flattenInversoresFromPlantaMinutes(
  uGs: PlantaUGAtMinutes[] | undefined,
  useUgPrefix: boolean,
): AggregatedInversor[] {
  if (!uGs?.length) return [];

  return uGs.flatMap((ug) => {
    const ugNumber = getNumberOfUgName(ug.name);
    const ugLabel = getUgDisplayName(ug.name, ugNumber);

    return (ug.inversores ?? []).map((inversor) => {
      const inverterNumber = getInverterNumber(inversor.name);
      return {
        name: formatInverterName(
          inverterNumber ?? 0,
          ugLabel,
          useUgPrefix,
          inversor.name,
        ),
        potenciaAtivaAtMinutes: inversor.potenciaAtivaAtMinutes ?? [],
      };
    });
  });
}

export function buildConfiguredAggregatedInversores(
  configuredUGs: UG[] | undefined,
  useUgPrefix: boolean,
): AggregatedInversor[] {
  if (!configuredUGs?.length) return [];

  return sortConfiguredUGs(configuredUGs).flatMap((configUg) => {
    const ugNumber = getNumberOfUgName(configUg.name);
    const ugLabel = getUgDisplayName(configUg.name, ugNumber);
    const firstInverterNumber =
      configUg.components.inverters?.firstInverterNumber ?? 1;
    const amount = configUg.components.inverters?.amount ?? 0;

    if (amount <= 0) return [];

    const maxInverterNumber = firstInverterNumber + amount - 1;
    const inversores: AggregatedInversor[] = [];

    for (let i = firstInverterNumber; i <= maxInverterNumber; i++) {
      inversores.push({
        name: formatInverterName(i, ugLabel, useUgPrefix),
        potenciaAtivaAtMinutes: [],
      });
    }

    return inversores;
  });
}

function indexInversoresByKey(
  inversores: AggregatedInversor[],
  ugNumberByName?: Map<string, number>,
): Map<string, AggregatedInversor> {
  const map = new Map<string, AggregatedInversor>();

  inversores.forEach((inversor) => {
    const identity: InverterIdentity = {
      ugNumber: ugNumberByName?.get(normalizeName(inversor.name)) ??
        getNumberOfUgName(inversor.name),
      inverterNumber: getInverterNumber(inversor.name),
    };
    map.set(getInverterKey(identity, inversor.name), inversor);
  });

  return map;
}

function buildUgNumberByNameFromPlantaMinutes(
  uGs: PlantaUGAtMinutes[] | undefined,
): Map<string, number> {
  const map = new Map<string, number>();

  uGs?.forEach((ug) => {
    const ugNumber = getNumberOfUgName(ug.name);
    if (ugNumber === undefined) return;

    ug.inversores?.forEach((inversor) => {
      if (inversor.name) {
        map.set(normalizeName(inversor.name), ugNumber);
      }
    });
  });

  return map;
}

function pickMinuteSeries(
  ...candidates: (BaseUsinaValue[] | undefined)[]
): BaseUsinaValue[] {
  for (const candidate of candidates) {
    if (hasMinuteSeries(candidate)) {
      return candidate ?? [];
    }
  }
  return candidates.find((candidate) => candidate !== undefined) ?? [];
}

function findInversorForBase(
  byKey: Map<string, AggregatedInversor>,
  inversores: AggregatedInversor[],
  baseInversor: AggregatedInversor,
  identity: InverterIdentity,
): AggregatedInversor | undefined {
  const key = getInverterKey(identity, baseInversor.name);
  const byKeyMatch = byKey.get(key);
  if (byKeyMatch) return byKeyMatch;

  const normalizedBaseName = normalizeEquipmentName(baseInversor.name);
  const byExactName = inversores.find(
    (inversor) => normalizeEquipmentName(inversor.name) === normalizedBaseName,
  );
  if (byExactName) return byExactName;

  if (identity.inverterNumber === undefined) return undefined;

  const candidates = inversores.filter(
    (inversor) => getInverterNumber(inversor.name) === identity.inverterNumber,
  );

  if (candidates.length === 1) {
    return candidates[0];
  }

  if (identity.ugNumber !== undefined) {
    return candidates.find(
      (inversor) => getNumberOfUgName(inversor.name) === identity.ugNumber,
    );
  }

  return undefined;
}

export function mergeAggregatedInversores(
  configuredUGs: UG[] | undefined,
  card19Inversores: AggregatedInversor[] | undefined,
  plantaMinutesUGs: PlantaUGAtMinutes[] | undefined,
): AggregatedInversor[] {
  const card19List = card19Inversores ?? [];
  const card19HasMinuteSeries = card19List.some((inversor) =>
    hasMinuteSeries(inversor.potenciaAtivaAtMinutes),
  );

  if (card19HasMinuteSeries && card19List.length > 0) {
    if (!configuredUGs?.length) {
      return card19List;
    }

    const configuredInversores = buildConfiguredAggregatedInversores(
      configuredUGs,
      shouldUseUgPrefixInNames(
        configuredUGs,
        card19List.map((inversor) => inversor.name ?? ""),
      ),
    );
    const card19Names = new Set(
      card19List.map((inversor) => normalizeEquipmentName(inversor.name)),
    );

    const missingConfiguredInversores = configuredInversores.filter(
      (inversor) => !card19Names.has(normalizeEquipmentName(inversor.name)),
    );

    return [...card19List, ...missingConfiguredInversores];
  }

  const mergedPlantaMinutes = mergePlantaUGsAtMinutesWithUsinaConfig(
    configuredUGs,
    plantaMinutesUGs,
  );
  const card13Inversores = flattenInversoresFromPlantaMinutes(
    mergedPlantaMinutes,
    shouldUseUgPrefixInNames(
      configuredUGs,
      [
        ...(card19Inversores?.map((inversor) => inversor.name ?? "") ?? []),
        ...mergedPlantaMinutes.flatMap((ug: PlantaUGAtMinutes) =>
          (ug.inversores ?? []).map((inversor) => inversor.name ?? ""),
        ),
      ],
    ),
  );

  const useUgPrefix = shouldUseUgPrefixInNames(
    configuredUGs,
    [
      ...(card19Inversores?.map((inversor) => inversor.name ?? "") ?? []),
      ...card13Inversores.map((inversor) => inversor.name ?? ""),
    ],
  );

  const configuredInversores = buildConfiguredAggregatedInversores(
    configuredUGs,
    useUgPrefix,
  );

  const baseList =
    configuredInversores.length > 0
      ? configuredInversores
      : [
          ...(card19Inversores ?? []),
          ...card13Inversores.filter(
            (card13Inversor) =>
              !(card19Inversores ?? []).some(
                (card19Inversor) =>
                  normalizeName(card19Inversor.name) ===
                  normalizeName(card13Inversor.name),
              ),
          ),
        ];

  if (!baseList.length) {
    return card19Inversores ?? card13Inversores ?? [];
  }

  const ugNumberByName = buildUgNumberByNameFromPlantaMinutes(mergedPlantaMinutes);
  const card13List = card13Inversores;
  const card19ByKey = indexInversoresByKey(card19List, ugNumberByName);
  const card13ByKey = indexInversoresByKey(card13List, ugNumberByName);

  return baseList.map((baseInversor) => {
    const identity: InverterIdentity = {
      ugNumber:
        ugNumberByName.get(normalizeName(baseInversor.name)) ??
        getNumberOfUgName(baseInversor.name),
      inverterNumber: getInverterNumber(baseInversor.name),
    };

    const card19 = findInversorForBase(
      card19ByKey,
      card19List,
      baseInversor,
      identity,
    );
    const card13 = findInversorForBase(
      card13ByKey,
      card13List,
      baseInversor,
      identity,
    );

    const potenciaAtivaAtMinutes = pickMinuteSeries(
      card19?.potenciaAtivaAtMinutes,
      card13?.potenciaAtivaAtMinutes,
      baseInversor.potenciaAtivaAtMinutes,
    );

    return {
      ...baseInversor,
      name: card19?.name ?? card13?.name ?? baseInversor.name,
      potenciaAtivaAtMinutes,
    };
  });
}

export function heatmapHasRenderableData(inversores: AggregatedInversor[]): boolean {
  return inversores.some((inversor) =>
    hasMinuteSeries(inversor.potenciaAtivaAtMinutes),
  );
}
